import React from "react";
import { ModernFolderCard } from "./ModernFolderCard";
import { ModernRiskCard } from "./ModernRiskCard";
import { ModernEmptyState } from "./ModernEmptyState";
import { Assessment } from "../../FolderContentsTypes";
import type { Database } from "@/integrations/supabase/types";

type FolderType = Database['public']['Tables']['folders']['Row'];

interface ModernContentGridProps {
  folderData: FolderType | null;
  subfolders: FolderType[];
  assessments: Assessment[];
  projectId: string;
  folderId: string;
  isShared: boolean;
  onOpenFolder: (folderId: string) => void;
  onNavigateToAssessment: (assessmentId: string, folderId: string) => void;
  onDeleteFolder: (folderId: string) => Promise<void>;
  onDeleteAssessment: (assessmentId: string) => Promise<void>;
  onCreateFolder: (name: string, description?: string) => Promise<void>;
  onStartAssessment?: (projectId: string, folderId: string) => void;
}

export const ModernContentGrid: React.FC<ModernContentGridProps> = ({
  folderData,
  subfolders,
  assessments,
  projectId,
  folderId,
  isShared,
  onOpenFolder,
  onNavigateToAssessment,
  onDeleteFolder,
  onDeleteAssessment,
  onCreateFolder,
  onStartAssessment
}) => {
  // Group assessments by type
  const riskAssessments = assessments.filter(a => a.type === 'risk_assessment');
  const skrAssessments = assessments.filter(a => a.type === 'skr_assessment');
  const workPreparations = assessments.filter(a => a.type === 'work_preparation');

  // DEBUG: Log assessment categorization
  console.log('🎯 CATEGORIZING ASSESSMENTS:');
  console.log('Total assessments received:', assessments.length);
  console.log('All assessment types:', assessments.map(a => ({ id: a.id, name: a.name, type: a.type })));
  console.log('Risk assessments filtered:', riskAssessments.length, riskAssessments.map(a => a.name));
  console.log('SKR assessments filtered:', skrAssessments.length, skrAssessments.map(a => a.name));
  console.log('Work preparations filtered:', workPreparations.length, workPreparations.map(a => a.name));

  const hasContent = subfolders.length > 0 || assessments.length > 0;

  if (!hasContent) {
    return (
      <ModernEmptyState
        folderData={folderData}
        projectId={projectId}
        folderId={folderId}
        isShared={isShared}
        onStartAssessment={onStartAssessment}
        onCreateFolder={onCreateFolder}
      />
    );
  }

  return (
    <div className="space-y-12">
      {/* Subfolders Section */}
      {subfolders.length > 0 && (
        <section className="space-y-6">
          <header className="hero-dark-gradient text-on-hero rounded-xl px-6 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg">
                <h3 className="text-lg font-semibold">Mappar</h3>
              </div>
              <div className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white text-sm rounded-lg font-medium border border-white/30">
                {subfolders.length}
              </div>
            </div>
          </header>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {subfolders.map(subfolder => (
              <div key={subfolder.id} className="animate-fade-in">
                <ModernFolderCard
                  folder={subfolder}
                  onOpen={() => onOpenFolder(subfolder.id)}
                  onDelete={() => onDeleteFolder(subfolder.id)}
                  assessmentCount={0}
                  subfolderCount={0}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SKR Assessments Section - MOVED TO TOP */}
      {skrAssessments.length > 0 && (
        <section className="space-y-6">
          <header className="hero-dark-gradient text-on-hero rounded-xl px-6 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg">
                <h3 className="text-lg font-semibold">SKR-bedömningar</h3>
              </div>
              <div className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white text-sm rounded-lg font-medium border border-white/30">
                {skrAssessments.length}
              </div>
            </div>
          </header>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {skrAssessments.map(assessment => (
              <div key={assessment.id} className="animate-fade-in">
                <ModernRiskCard
                  assessment={assessment}
                  onOpen={() => onNavigateToAssessment(assessment.assessment_id || assessment.id, folderId)}
                  onDelete={() => onDeleteAssessment(assessment.id)}
                  projectId={projectId}
                  folderId={folderId}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Risk Assessments Section - MOVED TO SECOND */}
      {riskAssessments.length > 0 && (
        <section className="space-y-6">
          <header className="hero-dark-gradient text-on-hero rounded-xl px-6 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg">
                <h3 className="text-lg font-semibold">Risksammanställningar</h3>
              </div>
              <div className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white text-sm rounded-lg font-medium border border-white/30">
                {riskAssessments.length}
              </div>
            </div>
          </header>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {riskAssessments.map(assessment => (
              <div key={assessment.id} className="animate-fade-in">
                <ModernRiskCard
                  assessment={assessment}
                  onOpen={() => onNavigateToAssessment(assessment.assessment_id || assessment.id, folderId)}
                  onDelete={() => onDeleteAssessment(assessment.id)}
                  projectId={projectId}
                  folderId={folderId}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Work Preparations Section */}
      {workPreparations.length > 0 && (
        <section className="space-y-6">
          <header className="hero-dark-gradient text-on-hero rounded-xl px-6 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg">
                <h3 className="text-lg font-semibold">Arbetsberedningar</h3>
              </div>
              <div className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white text-sm rounded-lg font-medium border border-white/30">
                {workPreparations.length}
              </div>
            </div>
          </header>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {workPreparations.map(assessment => (
              <div key={assessment.id} className="animate-fade-in">
                <ModernRiskCard
                  assessment={assessment}
                  onOpen={() => onNavigateToAssessment(assessment.assessment_id || assessment.id, folderId)}
                  onDelete={() => onDeleteAssessment(assessment.id)}
                  projectId={projectId}
                  folderId={folderId}
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};